import uploadApi from '_api/upload'
import noImage from '_images/noimage02.svg'
import { DEFAULT_SUB_ITEM, SHIFT_DOWN, SHIFT_UP } from '_utils/constant'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFieldArray } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import { useModal } from '../../../hooks/useModal'
import ProductSettingModal from '../../../pages/Event/components/ProductSettingModal'
import DesktopInputProduct from './DesktopInputProduct'
import DesktopSubItem from './DesktopSubItem'
import ModalSplitCasePurchase from './Modal/ModalSplitCasePurchase'

const OptionsPortal = ({ anchorRef, children, onClose }) => {
  const elRef = useRef(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const update = useCallback(() => {
    const el = anchorRef?.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX })
  }, [anchorRef])

  useLayoutEffect(() => {
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [update])

  useEffect(() => {
    const handler = (e) => {
      if (elRef.current?.contains(e.target)) return
      if (anchorRef?.current?.contains(e.target)) return
      onClose?.()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [anchorRef, onClose])

  return createPortal(
    <div
      ref={elRef}
      className='desktop-options-portal'
      style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 99999 }}
    >
      {children}
    </div>,
    document.body
  )
}

const DesktopProductItem = ({
  productIndex,
  product,
  totalListProduct,
  control,
  register,
  removeProduct,
  insertProduct,
  swapProduct,
  watch,
  setValue,
  errors,
}) => {
  const { addToast } = useToasts()

  const {
    fields: subFields,
    remove: removeSubItem,
    insert: insertSubItem,
    append: appendSubItem,
  } = useFieldArray({
    control,
    name: `products.${productIndex}.subItems`,
  })

  const watchSubItems = watch(`products.${productIndex}.subItems`)
  const controlledSubItems = useMemo(
    () => subFields.map((f, i) => ({ ...f, ...watchSubItems?.[i] })),
    [subFields, watchSubItems]
  )
  const orderConfigModal = useModal()
  const splitCaseModal = useModal()

  const [imgUrl, setImgUrl] = useState(product?.photoUrls?.[0] || '')
  useEffect(() => {
    setImgUrl(product?.photoUrls?.[0] || '')
  }, [product?.photoUrls?.[0]])

  const [showOptions, setShowOptions] = useState(false)
  const optionsRef = useRef(null)

  const handleDescriptionChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/\n{3,}/g, '\n')
      setValue(`products.${productIndex}.description`, value)
    },
    [productIndex, setValue]
  )

  const onImageUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0]
      if (!file) return
      try {
        const { msgResp } = await uploadApi.uploadPhoto(e.target.files)
        setImgUrl(URL.createObjectURL(file))
        setValue(`products.${productIndex}.photoUrls`, [msgResp.url])
      } catch {
        setImgUrl(URL.createObjectURL(file))
        setValue(`products.${productIndex}.photoUrls`, [URL.createObjectURL(file)])
      }
    },
    [productIndex, setValue]
  )

  const removeImage = useCallback(() => {
    setImgUrl('')
    setValue(`products.${productIndex}.photoUrls`, [])
  }, [productIndex, setValue])

  const handleSwap = useCallback(
    (direction) => {
      if (direction === SHIFT_UP && productIndex > 0) {
        swapProduct(productIndex, productIndex - 1)
      }
      if (direction === SHIFT_DOWN && productIndex + 1 < totalListProduct) {
        swapProduct(productIndex, productIndex + 1)
      }
    },
    [productIndex, swapProduct, totalListProduct]
  )

  const handleOption = useCallback(
    (key) => {
      setShowOptions(false)
      if (key === 'add-sub-items') {
        appendSubItem({ ...DEFAULT_SUB_ITEM, list: DEFAULT_SUB_ITEM.list.map((o) => ({ ...o })) })
      }
      if (key === 'order-config') {
        orderConfigModal.openModal()
      }
      if (key === 'split-case') {
        const price = watch(`products.${productIndex}.price`)
        if (!price || Number(price) <= 0) {
          addToast('Please enter a price first.', {
            appearance: 'error',
            autoDismiss: true,
          })
          return
        }
        splitCaseModal.openModal()
      }
    },
    [appendSubItem, orderConfigModal, splitCaseModal, watch, productIndex, addToast]
  )

  const productErrors = errors?.products?.[productIndex]

  return (
    <div className='desktop-product-item-wrapper'>
      <div className='desktop-product-item'>
        <div className='desktop-product-item__form'>
          {/* Image */}
          <div className='row align-items-center desktop-product-item__row'>
            <label className='col-3 desktop-product-item__label'>Item Image</label>
            <div className='col-9'>
              <div className='desktop-product-item__image-upload'>
                <div className='desktop-product-item__image-box'>
                  <img src={imgUrl || noImage} alt='product' />
                  <div className='desktop-product-item__image-overlay'>
                    <input
                      type='file'
                      accept='image/*'
                      className='desktop-product-item__image-input'
                      value=''
                      onChange={onImageUpload}
                    />
                  </div>
                </div>
                {imgUrl && (
                  <span className='desktop-product-item__image-remove' onClick={removeImage}>
                    &times;
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className='row mb-2'>
            <div className='col-3' />
            <div className='col-9'>
              {Boolean(product.splitCasePurchase?.itemsPerCase) && (
                <span className='c-txt-org' style={{ fontSize: '14px' }}>
                  {product.splitCasePurchase.itemsPerCase} per case/carton
                </span>
              )}
            </div>
          </div>

          {/* Name */}
          <div className='row desktop-product-item__row'>
            <label className='col-3 desktop-product-item__label'>Item name</label>
            <div className='col-9'>
              <DesktopInputProduct
                register={register(`products.${productIndex}.name`)}
                error={productErrors?.name?.message}
              />
            </div>
          </div>

          {/* Description */}
          <div className='row desktop-product-item__row'>
            <label className='col-3 desktop-product-item__label'>Description</label>
            <div className='col-9'>
              <textarea
                className={`form-control custom-scrollbar ${
                  productErrors?.description ? 'is-error' : ''
                }`}
                rows={3}
                placeholder='(Optional)'
                {...register(`products.${productIndex}.description`)}
                onChange={handleDescriptionChange}
              />
            </div>
          </div>

          {/* Price */}
          <div className='row desktop-product-item__row desktop-product-item__row--price'>
            <label className='col-3 desktop-product-item__label'>Price ($)</label>
            <div className='col-9'>
              <div className='desktop-product-item__price-row'>
                <DesktopInputProduct
                  type='number'
                  step='0.01'
                  register={register(`products.${productIndex}.price`)}
                  error={productErrors?.price?.message}
                />
                {/* Copy / Remove */}
                <div className='desktop-product-item__item-actions'>
                  <a
                    className='btn-copy cursor-pointer'
                    title='Duplicate product'
                    onClick={() =>
                      insertProduct(
                        productIndex + 1,
                        JSON.parse(JSON.stringify({ ...product, pid: '', photoUrls: [] }))
                      )
                    }
                  />
                  <a
                    className='btn-remove cursor-pointer'
                    title='Remove product'
                    onClick={() => removeProduct(productIndex)}
                  />
                </div>
              </div>

              {/* Add Options */}
              <div className='desktop-product-item__add-options-wrap'>
                <span
                  ref={optionsRef}
                  className='desktop-product-item__add-options'
                  onClick={() => setShowOptions((v) => !v)}
                >
                  Add Options
                </span>
                {showOptions && (
                  <OptionsPortal anchorRef={optionsRef} onClose={() => setShowOptions(false)}>
                    {[
                      { key: 'order-config', label: 'Order Configuration' },
                      { key: 'split-case', label: 'Split Case Purchase' },
                      { key: 'add-sub-items', label: 'Add sub-items' },
                    ].map((opt) => (
                      <div
                        key={opt.key}
                        className='desktop-options-portal__item'
                        onMouseDown={() => handleOption(opt.key)}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </OptionsPortal>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Arrows */}
        <div className='desktop-product-item__arrows'>
          <div className='arrow-drap arrow-drap-up' onClick={() => handleSwap(SHIFT_UP)} />
          <div className='arrow-drap arrow-drap-down' onClick={() => handleSwap(SHIFT_DOWN)} />
        </div>
      </div>
      {/* Sub-items */}
      {controlledSubItems.map((item, i) => (
        <DesktopSubItem
          key={i}
          productIndex={productIndex}
          nestIndex={i}
          subItem={item}
          {...{ control, register, removeSubItem, insertSubItem, appendSubItem, watch, errors }}
        />
      ))}
      {/* Order Config Modal */}
      <ProductSettingModal
        isOpen={orderConfigModal.isOpen}
        toggle={orderConfigModal.closeModal}
        register={register}
        productIndex={productIndex}
        errors={errors}
      />
      {/* Split Case Modal */}
      <ModalSplitCasePurchase
        isOpen={splitCaseModal.isOpen}
        toggle={splitCaseModal.closeModal}
        register={register}
        productIndex={productIndex}
        errors={errors}
        watch={watch}
        setValue={setValue}
        control={control}
      />
    </div>
  )
}

export default DesktopProductItem
